import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Slider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  makeStyles,
  useTheme
} from "@material-ui/core";
import {
  PlayArrow,
  Pause,
  Stop,
  VolumeUp,
  VolumeDown,
  VolumeOff,
  MoreVert,
  GetApp,
  FileCopy,
  Share,
  Speed
} from "@material-ui/icons";

import { format } from "date-fns";
import { i18n } from "../../translate/i18n";
import WaveformVisualization from "../WaveformVisualization";
import { downloadResource } from "../../utils";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0),
    borderRadius: theme.shape.borderRadius * 2,
    position: "relative",
    maxWidth: 400,
    minWidth: 280
  },
  userMessage: {
    alignSelf: "flex-end",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      right: -8,
      width: 0,
      height: 0,
      borderLeft: `8px solid ${theme.palette.primary.main}`,
      borderTop: "8px solid transparent"
    }
  },
  assistantMessage: {
    alignSelf: "flex-start",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: -8,
      width: 0,
      height: 0,
      borderRight: `8px solid ${theme.palette.background.paper}`,
      borderTop: "8px solid transparent"
    }
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1)
  },
  typeChip: {
    fontSize: 10,
    height: 20
  },
  timestamp: {
    fontSize: 10,
    opacity: 0.7
  },
  contentArea: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  audioControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  playButton: {
    width: 40,
    height: 40,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)"
    }
  },
  waveformContainer: {
    flex: 1,
    height: 40,
    cursor: "pointer",
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden"
  },
  progressBar: {
    flex: 1,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  timeDisplay: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 12,
    opacity: 0.8,
    marginTop: theme.spacing(0.5)
  },
  transcription: {
    padding: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: theme.shape.borderRadius,
    fontSize: 14,
    fontStyle: "italic",
    marginTop: theme.spacing(1)
  },
  voiceInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    fontSize: 12,
    opacity: 0.7
  },
  volumeControl: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 120
  },
  speedControl: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 100
  },
  menuItem: {
    fontSize: 14
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    borderRadius: theme.shape.borderRadius * 2
  }
}));

const AudioMessageComponent = ({
  message,
  isUser = false,
  onTranscriptionRequest,
  onShare,
  onDownload
}) => {
  const classes = useStyles();
  const theme = useTheme();
  
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [isMuted, setIsMuted] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [audioData, setAudioData] = useState(null);

  // Refs
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  // Format time helper
  const formatTime = useCallback((seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Initialize audio element
  useEffect(() => {
    if (message.audioUrl) {
      const audio = new Audio(message.audioUrl);
      audioRef.current = audio;

      audio.addEventListener("loadedmetadata", () => {
        setDuration(audio.duration);
        setIsLoading(false);
      });

      audio.addEventListener("timeupdate", () => {
        setCurrentTime(audio.currentTime);
      });

      audio.addEventListener("ended", () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });

      audio.addEventListener("error", (error) => {
        console.error("Audio error:", error);
        setIsLoading(false);
        toastError({ message: i18n.t("voiceAssistant.audioControls.error") });
      });

      audio.volume = volume;
      audio.playbackRate = playbackSpeed;
      audio.muted = isMuted;

      // Load audio data for waveform
      loadAudioData(message.audioUrl);

      return () => {
        audio.pause();
        audio.src = "";
      };
    }
  }, [message.audioUrl, volume, playbackSpeed, isMuted]);

  // Load audio data for waveform visualization
  const loadAudioData = useCallback(async (audioUrl) => {
    try {
      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Get audio data for visualization
      const channelData = audioBuffer.getChannelData(0);
      const samples = 100;
      const blockSize = Math.floor(channelData.length / samples);
      const filteredData = [];
      
      for (let i = 0; i < samples; i++) {
        let blockStart = blockSize * i;
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(channelData[blockStart + j]);
        }
        filteredData.push((sum / blockSize) * 255);
      }
      
      setAudioData(filteredData);
    } catch (error) {
      console.error("Error loading audio data:", error);
    }
  }, []);

  // Toggle play/pause
  const togglePlayback = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Playback error:", error);
          setIsLoading(false);
          toastError({ message: i18n.t("voiceAssistant.audioControls.error") });
        });
    }
  }, [isPlaying]);

  // Stop playback
  const stopPlayback = useCallback(() => {
    if (!audioRef.current) return;
    
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  // Seek to position
  const seekTo = useCallback((position) => {
    if (!audioRef.current || !duration) return;
    
    const newTime = (position / 100) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  // Handle volume change
  const handleVolumeChange = useCallback((event, newValue) => {
    setVolume(newValue);
    if (audioRef.current) {
      audioRef.current.volume = newValue;
    }
  }, []);

  // Handle speed change
  const handleSpeedChange = useCallback((speed) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
    setMenuAnchor(null);
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (audioRef.current) {
      audioRef.current.muted = newMuted;
    }
  }, [isMuted]);

  // Handle download
  const handleDownload = useCallback(() => {
    if (message.audioUrl) {
      if (onDownload) {
        onDownload(message);
      } else {
        downloadResource(message.audioUrl, `audio_message_${message.id}.webm`);
      }
    }
    setMenuAnchor(null);
  }, [message, onDownload]);

  // Handle share
  const handleShare = useCallback(() => {
    if (onShare) {
      onShare(message);
    } else {
      // Copy URL to clipboard
      navigator.clipboard.writeText(message.audioUrl).then(() => {
        toastUtils.success("URL copiada para área de transferência", "URL Copiada");
      });
    }
    setMenuAnchor(null);
  }, [message, onShare]);

  // Get progress percentage
  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <Paper
      className={`${classes.root} ${
        isUser ? classes.userMessage : classes.assistantMessage
      }`}
      elevation={2}
    >
      {isLoading && (
        <Box className={classes.loadingOverlay}>
          <CircularProgress size={24} color="inherit" />
        </Box>
      )}

      {/* Header */}
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            size="small"
            label={isUser ? i18n.t("voiceAssistant.voiceInterface.voiceMessage") : i18n.t("voiceAssistant.voiceInterface.aiResponse")}
            className={classes.typeChip}
            color={isUser ? "primary" : "default"}
          />
          {message.voice && !isUser && (
            <Typography variant="caption" className={classes.voiceInfo}>
              {i18n.t(`voiceAssistant.ttsSettings.voice.${message.voice}`)}
            </Typography>
          )}
        </Box>
        
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="caption" className={classes.timestamp}>
            {format(new Date(message.timestamp), "HH:mm")}
          </Typography>
          <IconButton
            size="small"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box className={classes.contentArea}>
        {/* Audio Controls */}
        <Box className={classes.audioControls}>
          <IconButton
            className={classes.playButton}
            onClick={togglePlayback}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : isPlaying ? (
              <Pause />
            ) : (
              <PlayArrow />
            )}
          </IconButton>

          <Box className={classes.waveformContainer}>
            <WaveformVisualization
              audioData={audioData}
              isRecording={false}
              isSpeaking={isPlaying}
              isProcessing={isLoading}
              audioLevel={isPlaying ? 0.5 : 0}
            />
          </Box>

          <IconButton size="small" onClick={stopPlayback}>
            <Stop fontSize="small" />
          </IconButton>
        </Box>

        {/* Progress Bar */}
        <Box className={classes.progressBar}>
          <Slider
            value={progress}
            onChange={(event, value) => seekTo(value)}
            min={0}
            max={100}
            step={0.1}
            size="small"
            color="primary"
          />
        </Box>

        {/* Time Display */}
        <Box className={classes.timeDisplay}>
          <Typography variant="caption">
            {formatTime(currentTime)}
          </Typography>
          <Typography variant="caption">
            {formatTime(duration)}
          </Typography>
        </Box>

        {/* Volume Control */}
        <Box className={classes.volumeControl}>
          <IconButton size="small" onClick={toggleMute}>
            {isMuted ? <VolumeOff /> : volume > 0.5 ? <VolumeUp /> : <VolumeDown />}
          </IconButton>
          <Slider
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            min={0}
            max={1}
            step={0.05}
            size="small"
            style={{ flex: 1 }}
          />
        </Box>

        {/* Transcription */}
        {message.content && (
          <Box className={classes.transcription}>
            <Typography variant="body2">
              {message.content}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Options Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleSpeedChange(0.5)} className={classes.menuItem}>
          <Speed fontSize="small" style={{ marginRight: 8 }} />
          0.5x
        </MenuItem>
        <MenuItem onClick={() => handleSpeedChange(0.75)} className={classes.menuItem}>
          <Speed fontSize="small" style={{ marginRight: 8 }} />
          0.75x
        </MenuItem>
        <MenuItem onClick={() => handleSpeedChange(1.0)} className={classes.menuItem}>
          <Speed fontSize="small" style={{ marginRight: 8 }} />
          1x
        </MenuItem>
        <MenuItem onClick={() => handleSpeedChange(1.25)} className={classes.menuItem}>
          <Speed fontSize="small" style={{ marginRight: 8 }} />
          1.25x
        </MenuItem>
        <MenuItem onClick={() => handleSpeedChange(1.5)} className={classes.menuItem}>
          <Speed fontSize="small" style={{ marginRight: 8 }} />
          1.5x
        </MenuItem>
        <MenuItem onClick={() => handleSpeedChange(2.0)} className={classes.menuItem}>
          <Speed fontSize="small" style={{ marginRight: 8 }} />
          2x
        </MenuItem>
        <MenuItem onClick={handleDownload} className={classes.menuItem}>
          <GetApp fontSize="small" style={{ marginRight: 8 }} />
          {i18n.t("voiceAssistant.audioControls.download")}
        </MenuItem>
        <MenuItem onClick={handleShare} className={classes.menuItem}>
          <Share fontSize="small" style={{ marginRight: 8 }} />
          {i18n.t("buttons.share")}
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default AudioMessageComponent;